"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const PRESETS = ["#ec4899", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];
const RAMP = [0.9, 0.78, 0.64, 0.5, 0.38, 0.27];

function hexToHsl(hex) {
    let h = hex.replace("#", "");
    if (h.length === 3) {
        h = h
            .split("")
            .map((c) => c + c)
            .join("");
    }
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    let hh = 0;
    let s = 0;
    if (d !== 0) {
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) hh = ((g - b) / d) % 6;
        else if (max === g) hh = (b - r) / d + 2;
        else hh = (r - g) / d + 4;
        hh *= 60;
        if (hh < 0) hh += 360;
    }
    return { h: hh, s, l };
}

function hslToHex(h, s, l) {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0;
    let g = 0;
    let b = 0;
    if (h < 60) {
        r = c;
        g = x;
    } else if (h < 120) {
        r = x;
        g = c;
    } else if (h < 180) {
        g = c;
        b = x;
    } else if (h < 240) {
        g = x;
        b = c;
    } else if (h < 300) {
        r = x;
        b = c;
    } else {
        r = c;
        b = x;
    }
    const to = (v) => {
        const n = Math.round((v + m) * 255);
        return n.toString(16).padStart(2, "0");
    };
    return "#" + to(r) + to(g) + to(b);
}

export default function SassColorGenerator() {
    const t = useTranslations();
    const [base, setBase] = useState("#ec4899");

    const { h, s } = hexToHsl(base);
    const shades = RAMP.map((l, i) => ({
        label: (i + 1) * 100,
        hex: hslToHex(h, s, l),
    }));

    return (
        <section id="sass-color-generator" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Sass_color_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Sass_color_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-pink-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:sass"
                            className="w-4 h-4"
                            aria-hidden="true"
                        />
                        _colors.scss
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Sass_color_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-8">
                    {/* Choix de la couleur de base */}
                    <div className="flex flex-wrap items-center gap-3 mb-5">
                        <span className="text-[13px] font-semibold text-gray-300">
                            {t("Sass_color_base_label")}
                        </span>
                        <label className="relative inline-flex items-center justify-center w-10 h-10 rounded-xl ring-1 ring-white/20 overflow-hidden cursor-pointer">
                            <span
                                className="absolute inset-0"
                                style={{ backgroundColor: base }}
                            />
                            <input
                                type="color"
                                value={base}
                                onChange={(e) => setBase(e.target.value)}
                                aria-label={t("Sass_color_base_label")}
                                className="opacity-0 w-full h-full cursor-pointer"
                            />
                        </label>
                        <div className="flex items-center gap-1.5">
                            {PRESETS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setBase(c)}
                                    aria-label={c}
                                    style={{ backgroundColor: c }}
                                    className={`w-7 h-7 rounded-lg ring-1 transition active:scale-90 ${
                                        base.toLowerCase() === c
                                            ? "ring-2 ring-white"
                                            : "ring-white/20"
                                    }`}
                                />
                            ))}
                        </div>
                        <span className="font-mono text-xs text-gray-500 uppercase">
                            {base}
                        </span>
                    </div>

                    {/* Snippet Sass illustratif */}
                    <pre className="rounded-2xl bg-black/40 ring-1 ring-white/10 p-4 text-[12.5px] leading-6 font-mono overflow-x-auto mb-5">
                        <code>
                            <span className="text-pink-300">$base</span>
                            <span className="text-gray-500">: </span>
                            <span className="text-emerald-300">{base}</span>
                            <span className="text-gray-500">;{"\n"}</span>
                            <span className="text-fuchsia-400">@for</span>
                            <span className="text-pink-300"> $i </span>
                            <span className="text-fuchsia-400">from</span>
                            <span className="text-amber-300"> 1 </span>
                            <span className="text-fuchsia-400">through</span>
                            <span className="text-amber-300"> 6 </span>
                            <span className="text-gray-500">{"{\n  "}</span>
                            <span className="text-sky-300">.brand-</span>
                            <span className="text-pink-300">#{"{"}$i{"}"}</span>
                            <span className="text-sky-300">00</span>
                            <span className="text-gray-500">{" { "}</span>
                            <span className="text-gray-300">background</span>
                            <span className="text-gray-500">: </span>
                            <span className="text-pink-300">scale-color</span>
                            <span className="text-gray-500">($base, ...);</span>
                            <span className="text-gray-500">{" }\n}"}</span>
                        </code>
                    </pre>

                    {/* Palette générée */}
                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink-300/90 mb-3">
                        {t("Sass_color_generated_label")}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5">
                        {shades.map((shade) => (
                            <div key={shade.label} className="min-w-0">
                                <div
                                    className="h-16 rounded-xl ring-1 ring-white/10 transition-colors duration-300"
                                    style={{ backgroundColor: shade.hex }}
                                />
                                <div className="mt-1.5 font-mono text-[11px] text-gray-300 truncate">
                                    .brand-{shade.label}
                                </div>
                                <div className="font-mono text-[10px] text-gray-500 uppercase">
                                    {shade.hex}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Sass_color_caption")}
            </p>
        </section>
    );
}
